import { ExternalLink, LayoutGrid, Lock, Mail, Search } from "lucide-react";
import * as React from "react";

import { type PortalCatalogEntry, portalLaunchUrl, useAppsCatalog } from "@/api/apps";
import { LogoWell } from "@/components/apps/LogoWell";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Apps launcher — the M9.5 entry point for the ATTMOS Suite
 * Unification surface (epic #340, Phase A), laid out to the
 * flowstack#1976 design.
 *
 * Phase A scope is launcher-only: clicking a card opens the portal
 * in a new tab via ``window.open(url, '_blank', 'noopener,noreferrer')``.
 * No iframe; no cross-origin CSP coordination needed. Iframe embedding
 * is deferred to P1.0+ per the launcher-only pivot.
 *
 * Entitlement is server-side — ``/api/apps`` only returns the portals
 * the session's Cognito groups grant access to (anchor §5). Whatever
 * shows up here, the user is entitled to launch.
 *
 * The page holds two groups. The ATTMOS Suite (the sibling portals) is
 * always there; the FlowStack apps group above it is filled by
 * flowstack#1971 and renders only once it has a card. "Find an app"
 * filters both, client side, by name or tagline.
 */
export function AppsLauncherPage(): JSX.Element {
  const { data: catalog, isLoading, isError, error } = useAppsCatalog();
  const [query, setQuery] = React.useState("");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Apps" description="Loading…" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-44" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Apps" />
        <EmptyState
          title="Could not load Apps."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  if (!catalog || catalog.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Apps" />
        <EmptyState
          icon={<LayoutGrid className="h-8 w-8 text-muted-foreground" aria-hidden />}
          title="No ATTMOS apps available to you."
          description="Contact a FlowStack admin if you expected to see apps here."
        />
      </div>
    );
  }

  const entitledCount = catalog.filter((p) => p.entitled).length;
  const visible = catalog.filter((portal) => matchesQuery(portal, query));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Apps"
        description={
          entitledCount === catalog.length
            ? `${catalog.length} apps entitled to your account. Click any tile to open in a new tab.`
            : `${entitledCount} of ${catalog.length} apps entitled. Locked tiles open a request-access email to the owning team.`
        }
        actions={<AppSearch value={query} onChange={setQuery} />}
      />
      {/* flowstack#1971 supplies the in-app cards; until then the group
          has none and renders nothing. */}
      <FlowStackAppsGroup>{[]}</FlowStackAppsGroup>
      <AppGroup title="ATTMOS Suite" description="Opens in a new tab with your ATTMOS sign-in.">
        {visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">{`No apps match “${query.trim()}”.`}</p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visible.map((portal) => (
              <li key={portal.slug}>
                <PortalTile portal={portal} />
              </li>
            ))}
          </ul>
        )}
      </AppGroup>
    </div>
  );
}

/** Case-insensitive substring match on the name or the tagline. */
function matchesQuery(portal: PortalCatalogEntry, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return (
    portal.display_name.toLowerCase().includes(needle) ||
    (portal.tagline ?? "").toLowerCase().includes(needle)
  );
}

function AppSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  // A visible label above the input, not a placeholder: placeholder text
  // vanishes the moment you type and is not a label to every reader.
  return (
    <div className="flex w-full flex-col gap-1.5 sm:w-64">
      <Label htmlFor="apps-search">Find an app</Label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          id="apps-search"
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 pl-9"
        />
      </div>
    </div>
  );
}

/**
 * One titled group of cards, as a labelled `region` so a landmark user can
 * jump between the FlowStack apps and the Suite. The heading is sentence
 * case with an optional plain sub-line under it; the region is named by the
 * heading alone and described by the sub-line, so a landmark user hears
 * both.
 */
function AppGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  const headingId = React.useId();
  const descriptionId = React.useId();
  return (
    <section
      aria-labelledby={headingId}
      aria-describedby={description ? descriptionId : undefined}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-col gap-0.5">
        <h2 id={headingId} className="font-display text-lg font-semibold text-foreground">
          {title}
        </h2>
        {description ? (
          <p id={descriptionId} className="text-sm text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/**
 * The FlowStack apps group — the in-app cards flowstack#1971 adds, above
 * the Suite. It renders NOTHING while it has no cards: an empty heading
 * would advertise a section with nothing in it. The cards themselves are
 * #1971's; this is only the slot they land in.
 */
export function FlowStackAppsGroup({
  children,
}: {
  children: readonly React.ReactElement[];
}): JSX.Element | null {
  if (children.length === 0) return null;
  return (
    <AppGroup title="FlowStack apps">
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{children}</ul>
    </AppGroup>
  );
}

/**
 * One Suite card: a uniform logo well, then the name, tagline and domain.
 *
 * Two states branched on ``portal.entitled``:
 *
 * - **Entitled** — anchor opens portal in a new tab via
 *   ``window.open(url, '_blank', 'noopener,noreferrer')``-equivalent
 *   markup. Hover lifts the card; the new-tab icon carries its own label.
 * - **Not entitled** — anchor opens a request-access mailto pointing
 *   at the portal's ``owner_contact.email`` with a pre-filled
 *   subject. Logo is desaturated + dimmed; Lock badge in the upper-
 *   right; "Request access" + Mail icon in footer.
 *
 * ``listed_in_catalog: False`` portals are hidden server-side per
 * anchor §4.1; they never reach this component.
 *
 * ``noopener,noreferrer`` on the entitled branch strips the
 * back-reference so the opened portal can't manipulate the
 * launcher (window.opener tampering).
 */
function PortalTile({ portal }: { portal: PortalCatalogEntry }): JSX.Element | null {
  if (portal.entitled) {
    const url = portalLaunchUrl(portal);
    // Same defense-in-depth gate as Phase E: a forward-compat / server
    // bug that lets an off-domain manifest through must not render an
    // unsafe link. Skip the tile rather than render a broken anchor.
    if (url === null) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "group block h-full rounded-2xl outline-none",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        )}
        aria-label={`Open ${portal.display_name} in a new tab`}
      >
        <TileChrome portal={portal} entitled />
      </a>
    );
  }

  // Not entitled — request-access mailto. Subject is properly
  // URL-encoded; email is interpolated raw because the backend
  // ``PortalManifest.owner_contact.email`` is validated as
  // ``EmailStr`` and can't contain mailto-breaking characters.
  // (Same reasoning + comment as Phase E's EntitlementPanel.)
  const subject = encodeURIComponent(`FlowStack: request access to ${portal.display_name}`);
  return (
    <a
      href={`mailto:${portal.owner_contact.email}?subject=${subject}`}
      className={cn(
        "group block h-full rounded-2xl outline-none",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      aria-label={`Email ${portal.owner_contact.team} to request access to ${portal.display_name}`}
    >
      <TileChrome portal={portal} entitled={false} />
    </a>
  );
}

function TileChrome({
  portal,
  entitled,
}: {
  portal: PortalCatalogEntry;
  entitled: boolean;
}): JSX.Element {
  return (
    <Card
      elevation="raised"
      className={cn(
        "relative flex h-full flex-col overflow-hidden rounded-2xl p-0",
        "transition-all duration-200",
        // `--elevation-accent` is the hover lift with the cyan already in
        // it; a `shadow-<colour>` utility cannot recolour a shadow that
        // lives in a CSS custom property (U1.2).
        entitled
          ? "group-hover:-translate-y-0.5 group-hover:border-cyan/40 group-hover:shadow-[var(--elevation-accent)]"
          : "group-hover:border-border group-hover:shadow"
      )}
    >
      {!entitled ? (
        <span
          className={cn(
            "absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full",
            "border border-border-subtle bg-card/90 px-2 py-0.5 text-[10px] font-medium",
            "text-muted-foreground backdrop-blur-sm"
          )}
        >
          <Lock className="h-3 w-3" aria-hidden />
          Locked
        </span>
      ) : null}
      <LogoWell icon={portal.icon} muted={!entitled} />
      <div className="flex flex-1 flex-col gap-1 border-t border-border-subtle p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-sm font-semibold leading-tight">{portal.display_name}</p>
          {entitled ? (
            <ExternalLink
              role="img"
              aria-label="Opens in a new tab"
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors",
                "group-hover:text-cyan"
              )}
            />
          ) : (
            <Mail
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors",
                "group-hover:text-foreground"
              )}
              aria-hidden
            />
          )}
        </div>
        {portal.tagline ? (
          <p className="line-clamp-2 text-xs text-muted-foreground">{portal.tagline}</p>
        ) : null}
        <p className="truncate font-mono text-[10px] text-muted-foreground">
          {entitled ? portal.embed.hostname : "Request access"}
        </p>
      </div>
    </Card>
  );
}
